import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getOrder, cancelOrder } from '../utils/api';
import { formatDate, ORDER_STATUS_COLORS } from '../utils/helpers';
import toast from 'react-hot-toast';

const STEPS = ['pending','confirmed','processing','shipped','delivered'];

export default function OrderDetailPage() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    getOrder(id).then(r => setOrder(r.data)).finally(() => setLoading(false));
  }, [id]);

  const handleCancel = async () => {
    if (!window.confirm('Cancel this order?')) return;
    setCancelling(true);
    try {
      const { data } = await cancelOrder(id, { reason: 'Cancelled by customer' });
      setOrder(data);
      toast.success('Order cancelled');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Cannot cancel');
    } finally { setCancelling(false); }
  };

  if (loading) return <div className="page-loader"><div className="spinner"/></div>;
  if (!order)  return <div className="page-loader"><h2>Order not found</h2></div>;

  const s = ORDER_STATUS_COLORS[order.status] || {};
  const stepIdx = STEPS.indexOf(order.status);

  return (
    <div className="container" style={{ padding:'1.5rem 1rem', maxWidth:800 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1.5rem', flexWrap:'wrap', gap:'0.75rem' }}>
        <div>
          <Link to="/orders" style={{ fontSize:'0.85rem', color:'var(--primary)' }}>← Back to Orders</Link>
          <h1 style={{ fontSize:'1.4rem', fontWeight:700, marginTop:'0.25rem' }}>Order #{order.orderNumber}</h1>
          <p style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>{formatDate(order.createdAt)}</p>
        </div>
        <span className="badge" style={{ background:s.bg, color:s.color, fontSize:'0.85rem', padding:'0.375rem 0.875rem' }}>{s.label}</span>
      </div>

      {/* Progress */}
      {!['cancelled','returned'].includes(order.status) && (
        <div className="card" style={{ padding:'1.5rem', marginBottom:'1rem' }}>
          <div style={{ display:'flex', justifyContent:'space-between', position:'relative' }}>
            <div style={{ position:'absolute', top:16, left:0, right:0, height:3, background:'var(--border)', zIndex:0 }}>
              <div style={{ height:'100%', background:'var(--primary)', width:`${Math.max(0,(stepIdx/(STEPS.length-1))*100)}%`, transition:'width 0.5s' }}/>
            </div>
            {STEPS.map((step, i) => (
              <div key={step} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6, flex:1, zIndex:1 }}>
                <div style={{ width:32, height:32, borderRadius:'50%', background: i <= stepIdx ? 'var(--primary)' : 'var(--border)', color: i <= stepIdx ? '#fff' : 'var(--text-muted)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:'0.8rem', border:'3px solid #fff', boxShadow:'0 0 0 2px '+(i<=stepIdx?'var(--primary)':'var(--border)') }}>
                  {i < stepIdx ? '✓' : i+1}
                </div>
                <span style={{ fontSize:'0.7rem', color: i<=stepIdx?'var(--primary)':'var(--text-muted)', fontWeight: i<=stepIdx?700:400, textTransform:'capitalize', textAlign:'center' }}>{step}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem', marginBottom:'1rem' }}>
        {/* Items */}
        <div className="card" style={{ padding:'1.25rem', gridColumn:'1/-1' }}>
          <h3 style={{ fontWeight:700, marginBottom:'0.875rem', fontSize:'0.95rem' }}>Order Items</h3>
          {order.items.map((item,i) => (
            <div key={i} style={{ display:'flex', gap:'0.75rem', alignItems:'center', padding:'0.5rem 0', borderBottom:'1px solid var(--border)' }}>
              <img src={item.image} alt={item.name} style={{ width:52, height:52, borderRadius:8, objectFit:'cover' }}/>
              <div style={{ flex:1 }}>
                <p style={{ fontWeight:600, fontSize:'0.9rem' }}>{item.name}</p>
                {item.variant && <p style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>{item.variant}</p>}
              </div>
              <span style={{ fontSize:'0.85rem', color:'var(--text-secondary)' }}>×{item.quantity}</span>
              <strong style={{ color:'var(--accent)' }}>৳{(item.price*item.quantity).toLocaleString()}</strong>
            </div>
          ))}
          <div style={{ display:'flex', justifyContent:'space-between', fontWeight:800, marginTop:'0.75rem', fontSize:'1.05rem' }}>
            <span>Total</span>
            <span style={{ color:'var(--accent)' }}>৳{order.totalPrice.toLocaleString()}</span>
          </div>
        </div>

        {/* Shipping address */}
        <div className="card" style={{ padding:'1.25rem' }}>
          <h3 style={{ fontWeight:700, marginBottom:'0.875rem', fontSize:'0.95rem' }}>📍 Delivery Address</h3>
          <p style={{ fontWeight:600 }}>{order.shippingAddress.name}</p>
          <p style={{ fontSize:'0.875rem', color:'var(--text-secondary)', marginTop:'0.25rem' }}>{order.shippingAddress.phone}</p>
          <p style={{ fontSize:'0.875rem', color:'var(--text-secondary)' }}>{order.shippingAddress.address}</p>
          <p style={{ fontSize:'0.875rem', color:'var(--text-secondary)' }}>{order.shippingAddress.district}, {order.shippingAddress.division}</p>
        </div>

        {/* Payment info */}
        <div className="card" style={{ padding:'1.25rem' }}>
          <h3 style={{ fontWeight:700, marginBottom:'0.875rem', fontSize:'0.95rem' }}>💳 Payment Info</h3>
          <p style={{ fontSize:'0.875rem', marginBottom:'0.5rem' }}><strong>Method:</strong> {order.paymentMethod?.toUpperCase()}</p>
          <p style={{ fontSize:'0.875rem', marginBottom:'0.5rem' }}><strong>Status:</strong> {order.isPaid ? <span style={{color:'var(--success)'}}>✓ Paid</span> : <span style={{color:'var(--warning)'}}>Pending</span>}</p>
          <p style={{ fontSize:'0.875rem' }}><strong>Items:</strong> ৳{order.itemsPrice?.toLocaleString()}</p>
          <p style={{ fontSize:'0.875rem' }}><strong>Shipping:</strong> ৳{order.shippingPrice}</p>
          {order.discountAmount > 0 && <p style={{ fontSize:'0.875rem', color:'var(--success)' }}><strong>Discount:</strong> -৳{order.discountAmount}</p>}
        </div>
      </div>

      {['pending','confirmed'].includes(order.status) && (
        <button className="btn btn-danger" onClick={handleCancel} disabled={cancelling}>
          {cancelling ? 'Cancelling…' : 'Cancel Order'}
        </button>
      )}
    </div>
  );
}
